import { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { initSocket, getSocket } from "shared/services/socket";
import { GetNotificationCountService } from "shared/services/notification";
import { handleErrors } from "shared/utils/helper";
import * as io from "socket.io-client";

const useNotificationCount = () => {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef<io.Socket | null>(null);
  const handleNotificationRef = useRef<((data: any) => void) | null>(null);
  const auth = useSelector((state: any) => state.root.auth);
  const userId = auth?.user?.user_id || auth?.user?.id;
  const location = useLocation();
  const isOnNotificationsPage = location.pathname.includes("/notifications");
  const prevIsOnNotificationsPageRef = useRef(isOnNotificationsPage);

  const fetchCount = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await GetNotificationCountService();
      const data = response.data?.data ?? response.data;
      const unreadCount = Number(data?.unread_count ?? 0);
      setCount(unreadCount);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    if (prevIsOnNotificationsPageRef.current && !isOnNotificationsPage) {
      fetchCount();
    }

    if (!isOnNotificationsPage) {
      fetchCount();
    }

    prevIsOnNotificationsPageRef.current = isOnNotificationsPage;
  }, [userId, isOnNotificationsPage, fetchCount]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    let socket = getSocket();

    if (!socket || !socket.connected) {
      socket = initSocket(
        typeof userId === "string" ? parseInt(userId, 10) : userId,
      );
    }

    socketRef.current = socket;

    if (!socket) {
      return;
    }

    const handleNotification = (data: any) => {

      const currentUserIdStr = userId?.toString();

      const generatorUserId =
        data.generator?.user_id?.toString() ||
        data.generator?.id?.toString() ||
        data.generator?.userId?.toString();

      const notificationType = data.type?.toLowerCase();
      const isChatNotification =
        notificationType === "comment_added" ||
        notificationType === "new_comment" ||
        notificationType === "reply_added" ||
        notificationType === "new_reply";

      let isFromCurrentUser = false;

      if (isChatNotification) {
        const commentUserId =
          data.comment?.user_id?.toString() ||
          data.comment?.user?.user_id?.toString() ||
          data.comment?.user?.id?.toString() ||
          data.comment?.userId?.toString() ||
          data.data?.comment?.user_id?.toString() ||
          data.data?.comment?.user?.user_id?.toString() ||
          data.data?.comment?.user?.id?.toString() ||
          data.metadata?.comment?.user_id?.toString() ||
          data.metadata?.comment?.user?.user_id?.toString();

        const replyUserId =
          data.reply?.user_id?.toString() ||
          data.reply?.user?.user_id?.toString() ||
          data.reply?.user?.id?.toString() ||
          data.reply?.userId?.toString() ||
          data.data?.reply?.user_id?.toString() ||
          data.data?.reply?.user?.user_id?.toString() ||
          data.data?.reply?.user?.id?.toString() ||
          data.metadata?.reply?.user_id?.toString() ||
          data.metadata?.reply?.user?.user_id?.toString();

        isFromCurrentUser =
          generatorUserId === currentUserIdStr ||
          commentUserId === currentUserIdStr ||
          replyUserId === currentUserIdStr;
      } else {
        isFromCurrentUser = generatorUserId === currentUserIdStr;
      }

      if (isFromCurrentUser) {
        return;
      }

      if (!isOnNotificationsPage) {
        setCount((prevCount) => {
          const newCount = prevCount + 1;
          return newCount;
        });
      }

      window.dispatchEvent(
        new CustomEvent("notification:new", { detail: data }),
      );
    };

    handleNotificationRef.current = handleNotification;

    const setupListener = () => {
      const currentSocket = socketRef.current;
      const currentHandler = handleNotificationRef.current;
      if (currentSocket && currentHandler) {
        currentSocket.off("notification", currentHandler);
        currentSocket.on("notification", currentHandler);
      }
    };

    const oldHandler = handleNotificationRef.current;
    if (oldHandler && socket) {
      socket.off("notification", oldHandler);
    }

    if (socket.connected) {
      setupListener();
    } else {
      const connectHandler = () => {
        setupListener();
      };
      socket.once("connect", connectHandler);
    }

    const reconnectHandler = () => {
      setupListener();
    };
    socket.on("reconnect", reconnectHandler);

    const disconnectHandler = (reason: string) => {
    };
    socket.on("disconnect", disconnectHandler);

    return () => {
      const currentSocket = socketRef.current;
      const currentHandler = handleNotificationRef.current;
      if (currentSocket && currentHandler) {
        currentSocket.off("notification", currentHandler);
        currentSocket.off("reconnect", reconnectHandler);
        currentSocket.off("disconnect", disconnectHandler);
      }
    };
  }, [userId, isOnNotificationsPage]);

  useEffect(() => {
    const handleRefreshCount = () => {
      fetchCount();
    };

    const handleSyncCount = (event: Event) => {
      const customEvent = event as CustomEvent<{ count: number }>;
      if (isOnNotificationsPage && customEvent.detail) {
        setCount(customEvent.detail.count);
      }
    };

    window.addEventListener("notification:refresh-count", handleRefreshCount);
    window.addEventListener("notification:sync-count", handleSyncCount);

    return () => {
      window.removeEventListener(
        "notification:refresh-count",
        handleRefreshCount,
      );
      window.removeEventListener("notification:sync-count", handleSyncCount);
    };
  }, [fetchCount, isOnNotificationsPage]);

  return { count, loading, refreshCount: fetchCount };
};

export default useNotificationCount;
